
import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  IconButton, Button, TextField, MenuItem, Dialog, DialogTitle,
  DialogContent, DialogActions, Box, Select, InputLabel, FormControl
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SearchIcon from '@mui/icons-material/Search';
import { apiFetch } from '../utils/api';
import { useNavigate } from 'react-router-dom';

const statusOptions = ['active', 'inactive', 'cancelled'];

const SubscriptionManager = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [filteredSubs, setFilteredSubs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);

  const [form, setForm] = useState({ userId: '', plan: '', status: 'active', endDate: '' });
  const [editId, setEditId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
    fetchSubscriptions();
  }, []);

  const fetchUsers = async () => {
    const res = await apiFetch('/api/admin/users');
    if (res.success) setUsers(res.data);
  };

  const fetchSubscriptions = async () => {
    const res = await apiFetch('/api/admin/subscriptions');
    if (res.success) {
      setSubscriptions(res.data);
      setFilteredSubs(res.data);
    }
  };

  const handleDelete = async (id) => {
    await apiFetch(`/api/admin/subscriptions/${id}`, 'DELETE');
    fetchSubscriptions();
  };

  const handleSubmit = async () => {
    if (editId) {
      await apiFetch(`/api/admin/subscriptions/${editId}`, 'PUT', form);
    } else {
      await apiFetch('/api/admin/subscriptions', 'POST', form);
    }
    setDialogOpen(false);
    setEditId(null);
    setForm({ userId: '', plan: '', status: 'active', endDate: '' });
    fetchSubscriptions();
  };

  const handleEdit = (sub) => {
    setForm({
      userId: sub.userId?._id || '',
      plan: sub.plan,
      status: sub.status,
      endDate: sub.endDate?.slice(0, 10) || ''
    });
    setEditId(sub._id);
    setDialogOpen(true);
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    const filtered = subscriptions.filter(sub =>
      sub.userId?.username?.toLowerCase().includes(term) ||
      sub.userId?.email?.toLowerCase().includes(term)
    );
    setFilteredSubs(filtered);
  };

  return (
    <Container>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/nomas')}>
          Back to Admin Dashboard
        </Button>
        <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setDialogOpen(true)}>
          Add Subscription
        </Button>
      </Box>

      <Typography variant="h5" gutterBottom>Subscription Manager</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <SearchIcon />
        <TextField
          fullWidth
          label="Search by username or email"
          value={searchTerm}
          onChange={handleSearch}
        />
      </Box>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>User</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Plan</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>End Date</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredSubs.map(sub => (
            <TableRow key={sub._id}>
              <TableCell>{sub.userId?.username}</TableCell>
              <TableCell>{sub.userId?.email}</TableCell>
              <TableCell>{sub.plan}</TableCell>
              <TableCell>{sub.status}</TableCell>
              <TableCell>{sub.endDate ? new Date(sub.endDate).toLocaleDateString() : 'N/A'}</TableCell>
              <TableCell>
                <IconButton onClick={() => handleEdit(sub)}><EditIcon /></IconButton>
                <IconButton onClick={() => handleDelete(sub._id)}><DeleteIcon /></IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{editId ? 'Edit Subscription' : 'Create Subscription'}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>User</InputLabel>
            <Select
              value={form.userId}
              onChange={e => setForm({ ...form, userId: e.target.value })}
              label="User"
            >
              {users.map(user => (
                <MenuItem key={user._id} value={user._id}>
                  {user.username} ({user.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Plan"
            value={form.plan}
            onChange={e => setForm({ ...form, plan: e.target.value })}
            margin="dense"
          />
          <TextField
            fullWidth
            select
            label="Status"
            value={form.status}
            onChange={e => setForm({ ...form, status: e.target.value })}
            margin="dense"
          >
            {statusOptions.map(status => (
              <MenuItem key={status} value={status}>{status}</MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            label="End Date"
            type="date"
            value={form.endDate}
            onChange={e => setForm({ ...form, endDate: e.target.value })}
            margin="dense"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SubscriptionManager;


